import { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import RoleTabs from '@/components/feature/RoleTabs';
import PlanCard from '@/components/feature/PlanCard';
import { useAuth } from '@/context/AuthContext';
import {
  getPlans,
  getMySubscription,
  subscribeToPlan,
  ROLE_META,
  formatPrice,
} from '@/lib/subscription';
import type { Plan, Subscription } from '@/lib/subscription';

function DashboardSubscription() {
  const { user, profile } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [activeRole, setActiveRole] = useState('business');
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const userRole = profile?.role ?? 'user';

  const loadCurrent = useCallback(async () => {
    if (!user) return;
    const { subscription: sub, plan } = await getMySubscription(user.id);
    setSubscription(sub);
    setCurrentPlan(plan);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setActiveRole(userRole);
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getPlans();
        if (mounted) setPlans(data);
        await loadCurrent();
      } catch (err) {
        if (mounted) setError('Impossible de charger les offres.');
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user, userRole, loadCurrent]);

  const handleChoose = async (plan: Plan) => {
    if (!user) return;
    setError(null);
    setSuccess(null);
    setSubmittingId(plan.id);
    try {
      await subscribeToPlan(plan.id);
      setSuccess(`Vous êtes maintenant abonné à l'offre ${plan.name} (${formatPrice(plan.price_monthly)}/mois).`);
      await loadCurrent();
    } catch (err) {
      setError('Une erreur est survenue lors du choix de l\'offre. Veuillez réessayer.');
      console.error(err);
    } finally {
      setSubmittingId(null);
    }
  };

  const visiblePlans = plans.filter((p) => p.role === activeRole);
  const activeMeta = ROLE_META[activeRole];

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
          Mon abonnement
        </h1>
        <p className="text-sm text-foreground-400 mt-1">
          Choisissez l'offre qui correspond à votre activité. Le paiement en ligne sera bientôt
          disponible.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-accent-100/70 text-accent-900 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-900 text-sm">{success}</div>
      )}

      {/* Current subscription */}
      {currentPlan && (
        <div className="mb-8 p-5 md:p-6 rounded-xl bg-gradient-to-br from-primary-500/15 to-accent-500/10 border border-background-200/70">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/10">
                <i className="ri-vip-crown-line text-xl text-accent-400" />
              </div>
              <div>
                <p className="text-xs text-foreground-400">Offre actuelle</p>
                <p className="font-heading font-semibold text-lg text-foreground-50">
                  {currentPlan.name} · {ROLE_META[currentPlan.role]?.label ?? currentPlan.role}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading font-bold text-xl text-foreground-50">
                {formatPrice(currentPlan.price_monthly)}
                <span className="text-sm font-normal text-foreground-400"> /mois</span>
              </p>
              {subscription && (
                <p className="text-xs text-foreground-400">
                  depuis le {new Date(subscription.start_date).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role tabs */}
      <div className="flex flex-col items-start gap-3 mb-6">
        <RoleTabs activeRole={activeRole} onChange={setActiveRole} />
        <p className="text-sm text-foreground-400">
          {activeMeta
            ? `Offres pour les ${activeMeta.label.toLowerCase()}s`
            : 'Sélectionnez un profil'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : (
        <div
          className={`grid grid-cols-1 gap-6 ${
            visiblePlans.length > 1 ? 'md:grid-cols-2' : 'md:max-w-md'
          }`}
        >
          {visiblePlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              current={currentPlan?.id === plan.id}
              actionLabel="Choisir cette offre"
              onAction={() => handleChoose(plan)}
              loading={submittingId === plan.id}
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

export default DashboardSubscription;